import { useNavigate } from 'react-router-dom'
import { useExpenses, useDeleteExpense } from '../../hooks/useExpenses'
import { usePrinters } from '../../hooks/usePrinters'
import { formatMoney, formatDate, monthKey } from '../../lib/format'
import type { Expense } from '../../lib/database.types'
import { Skeleton, SwipeRow } from '../../components/ui'

export default function ExpensesList() {
  const { data: expenses, isLoading } = useExpenses()
  const { data: printers } = usePrinters()
  const deleteExpense = useDeleteExpense()
  const navigate = useNavigate()

  if (isLoading) return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  const printerNameById = new Map((printers ?? []).map((p) => [p.id, p.name]))

  const groups = groupByMonth(expenses ?? [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <button
          type="button"
          onClick={() => navigate('/money/expenses/new')}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Log expense
        </button>
      </div>

      {Object.keys(groups).length === 0 ? (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          No expenses yet — tap &quot;Log expense&quot; to add your first.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
        {Object.entries(groups)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([month, list]) => {
            const total = list.reduce((sum, e) => sum + Number(e.amount), 0)
            return (
              <div key={month} style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-surface-2)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  marginBottom: 'var(--space-2)',
                }}>
                  <span>{monthLabel(month)}</span>
                  <span>{formatMoney(-total)}</span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {list.map((e) => (
                    <li key={e.id} style={{ marginBottom: 'var(--space-2)' }}>
                      <SwipeRow
                        onEdit={() => navigate(`/money/expenses/${e.id}/edit`)}
                        onDelete={() => deleteExpense.mutate(e.id)}
                        deleteConfirmText="Delete this expense?"
                      >
                        <div style={{ background: 'var(--color-surface)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontWeight: 600 }}>{e.description || e.category}</div>
                              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                                {formatDate(e.expense_date)} · {e.category} · {e.printer_id ? printerNameById.get(e.printer_id) ?? 'unknown printer' : 'shared'}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: 'var(--color-danger)' }}>{formatMoney(-Number(e.amount))}</div>
                            </div>
                          </div>
                        </div>
                      </SwipeRow>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function groupByMonth(expenses: Expense[]): Record<string, Expense[]> {
  const groups: Record<string, Expense[]> = {}
  for (const e of expenses) {
    const key = monthKey(e.expense_date)
    if (!groups[key]) groups[key] = []
    groups[key].push(e)
  }
  return groups
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}
